/**
 * @fileoverview Web端可编辑表格组件
 * @description 这是一个基于Ant Design Table组件的可编辑表格，提供完整的增删改查功能。
 * 采用模态弹窗的形式进行行编辑，支持多种字段类型和数据验证，适用于Web端数据录入场景。
 *
 * 主要特性：
 * - 基于Ant Design Table，提供标准的表格体验
 * - 支持多种字段类型：文本、数字、百分比、国籍选择
 * - 模态弹窗编辑模式，避免内联编辑的复杂性
 * - 最小行数控制，确保表格基本结构
 * - 受控组件模式，支持外部状态管理
 * - 空行自动过滤和占位显示
 * - 完整的操作按钮（编辑、删除）
 */

import React, { useMemo, useState } from "react";
import { Button, Table } from "antd";
import { i18nCrossUtil } from "@adgm/libs";
import Image from "next/image";
import { ModalRowEditor } from "./common";
import styles from "@aspen/theme/Profile.module.less";
import editIcon from "../../assets/images/icon_edit.webp";
import trashIcon from "../../assets/images/icon_trash.webp";
import type { IEditableTableProps, IRowData } from "@adgm/cross-models";

/**
 * Web端可编辑表格组件
 * @description 基于Ant Design Table的可编辑表格，提供完整的数据管理功能
 * @param props 组件属性
 * @returns JSX.Element
 */
export const EditableTable: React.FC<IEditableTableProps> = ({
  value = [],
  onChange,
  columns,
  addButtonText,
  minRows = 2,
  maxRows,
  descriptionText,
}) => {
  const crossIntl = i18nCrossUtil.t();

  // 模态弹窗显示状态
  const [modalVisible, setModalVisible] = useState(false);
  // 当前编辑的数据
  const [editData, setEditData] = useState<IRowData | null>(null);
  // 当前编辑行的索引
  const [currentEditIndex, setCurrentEditIndex] = useState<number>();

  /**
   * 表格显示数据处理
   * @description 过滤空行数据，并补齐最小行数要求
   * 确保表格始终显示足够的行数，提供良好的用户体验
   */
  const tableData = useMemo(() => {
    // 过滤出有效数据（至少包含一个非空字段）
    const validData = value.filter(item =>
      Object.values(item).some(val => val && val.toString().trim()),
    );

    // 复制有效数据用于显示
    const displayData = [...validData];

    // 补齐最小行数，填充空对象作为占位行
    while (displayData.length < minRows) {
      displayData.push({});
    }

    return displayData;
  }, [value, minRows]);

  /**
   * 处理添加新行
   * @description 检查最大行数限制，打开新增模态弹窗
   */
  const handleAdd = () => {
    // 检查是否超过最大行数限制
    if (maxRows && value.length >= maxRows) {
      // TODO:kyc, 提示文案i18n后续处理
      return;
    }

    // 清空编辑数据，标识为新增模式
    setEditData(null);
    setModalVisible(true);
  };

  /**
   * 处理编辑行数据
   * @description 检查行是否为空，决定是编辑还是新增
   * @param record 要编辑的行数据
   * @param index 行索引
   */
  const handleEdit = (record: IRowData, index: number) => {
    // 检查是否为空行（所有字段都为空）
    if (!Object.values(record).some(val => val && val.toString().trim())) {
      // 空行直接进入新增模式
      handleAdd();
      return;
    }

    // 设置编辑数据和索引
    setEditData(record);
    setCurrentEditIndex(index);
    setModalVisible(true);
  };

  /**
   * 处理删除行
   * @description 根据索引删除指定行，触发onChange回调
   * @param _ 行数据（未使用）
   * @param index 要删除的行索引
   */
  const handleDelete = (_, index: number) => {
    const updatedData = [...value];
    updatedData.splice(index, 1);
    onChange?.(updatedData);
  };

  /**
   * 处理模态弹窗提交
   * @description 根据编辑模式（新增/编辑）更新数据
   * @param data 提交的表单数据
   */
  const handleModalSubmit = (data: IRowData) => {
    let updatedData;

    if (editData) {
      // 编辑模式：更新指定索引的数据
      updatedData = [...value];
      if (currentEditIndex !== undefined) {
        updatedData[currentEditIndex] = data;
      }
    } else {
      // 新增模式：过滤空行后添加新数据
      updatedData = [
        ...value.filter(item => Object.values(item).some(val => val && val.toString().trim())),
        { ...data },
      ];
    }

    // 触发数据变化回调
    onChange?.(updatedData);

    // 关闭模态弹窗并清理状态
    setModalVisible(false);
    setEditData(null);
  };

  /**
   * 处理模态弹窗取消
   * @description 关闭弹窗并清理编辑状态
   */
  const handleModalCancel = () => {
    setModalVisible(false);
    setEditData(null);
  };

  /**
   * 判断是否为空行
   * @description 检查行数据是否所有字段都为空
   * @param record 行数据
   * @returns 是否为空行
   */
  const isEmptyRow = (record: IRowData) => {
    return !Object.values(record).some(val => val && val.toString().trim());
  };

  /**
   * 表格列配置生成
   * @description 基于传入的列配置生成Ant Design Table所需的列配置
   * 包括数据列和操作列
   */
  const tableColumns = useMemo(() => {
    // 生成数据列配置
    const cols = columns.map(col => ({
      title: col.title,
      dataIndex: col.dataIndex,
      key: col.key,
      render:
        col.render ||
        ((value, _) => {
          // 默认的简单文本渲染
          const hasValue = value && value.toString().trim();
          return (
            <span className={hasValue ? styles.validText : styles.emptyText}>{value || ""}</span>
          );
        }),
    }));

    // 添加操作列
    cols.push({
      title: crossIntl["shareholder.table.actions"],
      key: "actions",
      dataIndex: "",
      width: 120,
      render: (_, record: IRowData, index: number) => {
        // 空行不显示操作按钮
        if (isEmptyRow(record)) {
          return null;
        }

        return (
          <div className={styles.actionButtons}>
            {/* 编辑按钮 */}
            <Button
              type="link"
              onClick={() => handleEdit(record, index)}
              size="small"
              title={crossIntl["table.button.edit"]}>
              <Image unoptimized src={editIcon} alt="" width={16} height={16} />
            </Button>

            {/* 删除按钮 */}
            <Button
              type="link"
              onClick={() => handleDelete(record, index)}
              size="small"
              danger
              title={crossIntl["table.button.delete"]}>
              <Image unoptimized src={trashIcon} alt="" width={16} height={16} />
            </Button>
          </div>
        );
      },
    });

    return cols;
  }, [columns, crossIntl]);

  return (
    <div className={styles.editableTable}>
      {/* Ant Design 表格组件 */}
      <Table
        columns={tableColumns}
        dataSource={tableData}
        rowKey={(_, index) => `row_${index}`}
        pagination={false}
        size="middle"
        rowClassName={record => (isEmptyRow(record) ? styles.emptyRow : "")}
      />

      {/* 表格底部操作区 */}
      <div className={styles.tableHeader}>
        <Button type="ghost" onClick={handleAdd} className={styles.addButton}>
          +{addButtonText || crossIntl["table.button.add"]}
        </Button>
      </div>

      {/* 编辑模态弹窗 */}
      <ModalRowEditor
        visible={modalVisible}
        editData={editData}
        columns={columns}
        onCancel={handleModalCancel}
        onSubmit={handleModalSubmit}
        descriptionText={descriptionText}
      />
    </div>
  );
};
